/**
 * Recurrence picker logic — form state, the saved `RecurringConfig`, and the
 * summary line — kept outside React so it can be tested on its own.
 *
 * The form holds more than the saved config: each mode (daily / weekly /
 * monthly) keeps its own interval, so flipping between tabs does not throw
 * away what was picked on the other one. Only the active mode's values are
 * written to the meeting.
 */

import { addDays, addMonths, addYears, format, getDaysInMonth, isValid, parse, startOfMonth } from 'date-fns'

import type {
  RecurrenceEndType,
  RecurringConfig,
  RecurringFrequency,
} from '../models/meeting.model'
import { startOfDay } from './schedule-time'

/**
 * The locale to hand `Intl` for a UI language. Only English needs a region,
 * and two of them: US English for dates ("Sep 28, 2026"), British English for
 * lists, whose "Mon, Wed and Fri" has no serial comma.
 */
const intlLocale = (lang: string, purpose: 'date' | 'list' = 'date') =>
  lang === 'en' ? (purpose === 'list' ? 'en-GB' : 'en-US') : lang

/** The app's `t` function: a translation key and its `{placeholder}` values. */
export type Translate = (key: string, vars?: Record<string, string | number>) => string

/** Largest "every N" each mode offers. */
export const RECURRENCE_MAX_INTERVAL: Record<RecurringFrequency, number> = {
  daily: 16,
  weekly: 12,
  monthly: 3,
}

/** Largest "end after N meetings". */
export const RECURRENCE_MAX_OCCURRENCES = 60

/** How far past the first meeting an end date may fall. The server holds the same limit. */
export const RECURRENCE_MAX_END_YEARS = 1

export const RECURRENCE_FREQUENCIES: readonly RecurringFrequency[] = ['daily', 'weekly', 'monthly']

export interface RecurrenceState {
  frequency: RecurringFrequency
  intervals: Record<RecurringFrequency, number>
  /** 0 (Sun) – 6 (Sat), kept sorted. Never empty. */
  weekDays: number[]
  /** 1 – 31. */
  monthDay: number
  endType: RecurrenceEndType
  occurrences: number
  /** Local midnight of the last day of the series. */
  endDate?: Date
  /**
   * Whether the weekdays / day of month were picked by hand. Until they are,
   * they follow the meeting's start date, so moving the start from a Monday
   * to a Tuesday moves the default weekday with it.
   */
  weekDaysChosen: boolean
  monthDayChosen: boolean
}

const clamp = (n: number, min: number, max: number, fallback: number) =>
  Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback

/** `yyyy-MM-dd` in local time, read back as local midnight. */
export function parseLocalDate(value: string | undefined): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const d = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(d) ? d : undefined
}

export function defaultRecurrence(start: Date): RecurrenceState {
  return {
    frequency: 'daily',
    intervals: { daily: 1, weekly: 1, monthly: 1 },
    weekDays: [start.getDay()],
    monthDay: start.getDate(),
    endType: 'count',
    occurrences: 1,
    endDate: undefined,
    weekDaysChosen: false,
    monthDayChosen: false,
  }
}

/**
 * Form state for a saved config. Tolerates records written before monthDay /
 * endType / endDate existed, and clamps values the dropdowns cannot show.
 */
export function recurrenceFromConfig(cfg: RecurringConfig | undefined, start: Date): RecurrenceState {
  const base = defaultRecurrence(start)
  if (!cfg) return base

  const frequency = RECURRENCE_FREQUENCIES.includes(cfg.frequency) ? cfg.frequency : base.frequency
  const weekDays = Array.from(new Set((cfg.weekDays ?? []).filter((d) => d >= 0 && d <= 6))).sort((a, b) => a - b)
  const monthDay = clamp(Number(cfg.monthDay), 1, 31, base.monthDay)
  const endDate = parseLocalDate(cfg.endDate)

  return {
    ...base,
    frequency,
    intervals: {
      ...base.intervals,
      [frequency]: clamp(Number(cfg.interval), 1, RECURRENCE_MAX_INTERVAL[frequency], 1),
    },
    weekDays: weekDays.length ? weekDays : base.weekDays,
    monthDay,
    endType: cfg.endType === 'date' && endDate ? 'date' : 'count',
    occurrences: clamp(Number(cfg.occurrences), 1, RECURRENCE_MAX_OCCURRENCES, 1),
    endDate,
    // A saved value equal to what the start date would give is still a default.
    weekDaysChosen: !(weekDays.length === 0 || (weekDays.length === 1 && weekDays[0] === start.getDay())),
    monthDayChosen: monthDay !== start.getDate(),
  }
}

export function recurrenceToConfig(s: RecurrenceState): RecurringConfig {
  return {
    frequency: s.frequency,
    interval: s.intervals[s.frequency],
    weekDays: s.frequency === 'weekly' ? [...s.weekDays] : [],
    occurrences: s.occurrences,
    ...(s.frequency === 'monthly' ? { monthDay: s.monthDay } : {}),
    endType: s.endType,
    ...(s.endType === 'date' && s.endDate ? { endDate: format(s.endDate, 'yyyy-MM-dd') } : {}),
  }
}

/** Re-derive the defaults the user has not overridden from a new start date. */
export function followStartDate(s: RecurrenceState, start: Date): RecurrenceState {
  return {
    ...s,
    weekDays: s.weekDaysChosen ? s.weekDays : [start.getDay()],
    monthDay: s.monthDayChosen ? s.monthDay : start.getDate(),
  }
}

/**
 * Switch the Daily / Weekly / Monthly tab. Everything starts over from the
 * defaults (interval, weekdays, day of month and "End after"), so a tab never
 * comes back with choices made on an earlier visit.
 */
export function switchFrequency(s: RecurrenceState, frequency: RecurringFrequency, start: Date): RecurrenceState {
  if (frequency === s.frequency) return s
  return { ...defaultRecurrence(start), frequency }
}

/** Toggle a weekday. The last remaining day cannot be removed. */
export function toggleWeekDay(s: RecurrenceState, day: number): RecurrenceState {
  const on = s.weekDays.includes(day)
  if (on && s.weekDays.length === 1) return s
  const weekDays = on ? s.weekDays.filter((d) => d !== day) : [...s.weekDays, day].sort((a, b) => a - b)
  return { ...s, weekDays, weekDaysChosen: true }
}

export type RecurrenceEndDateProblem = 'required' | 'beforeStart' | 'noMeetings' | 'tooFar'

type PatternFields = Partial<Pick<RecurringConfig, 'frequency' | 'interval' | 'weekDays' | 'monthDay'>>

/**
 * The day of the series' first meeting, laid out as the server does it: the
 * start day for daily; the first ticked weekday on or after it for weekly
 * (else the first one `interval` weeks on); the chosen day of month, or the
 * month's last day, for monthly, in the start month if it has not passed.
 */
export function firstMeetingDay(cfg: PatternFields, start: Date): Date {
  const day = startOfDay(start)
  const interval = cfg.interval && cfg.interval > 0 ? cfg.interval : 1
  if (cfg.frequency === 'weekly') {
    const weekDays = cfg.weekDays?.length ? [...cfg.weekDays].sort((a, b) => a - b) : [day.getDay()]
    const weekStart = addDays(day, -day.getDay())
    for (const wd of weekDays) {
      const d = addDays(weekStart, wd)
      if (d >= day) return d
    }
    return addDays(weekStart, 7 * interval + weekDays[0])
  }
  if (cfg.frequency === 'monthly') {
    const want = cfg.monthDay ?? day.getDate()
    const inMonth = (m: Date) => new Date(m.getFullYear(), m.getMonth(), Math.min(want, getDaysInMonth(m)))
    const here = inMonth(day)
    return here >= day ? here : inMonth(addMonths(startOfMonth(day), interval))
  }
  return day
}

/** What is wrong with the end date, if the series ends on one. */
export function recurrenceEndDateProblem(
  cfg: (Pick<RecurringConfig, 'endType' | 'endDate'> & PatternFields) | undefined,
  start: Date | undefined,
): RecurrenceEndDateProblem | null {
  if (!cfg || cfg.endType !== 'date') return null
  const end = parseLocalDate(cfg.endDate)
  if (!end) return 'required'
  if (start && end < startOfDay(start)) return 'beforeStart'
  // Monthly on the 30th from the 29th, ending the 29th: no meeting at all.
  if (start && end < firstMeetingDay(cfg, start)) return 'noMeetings'
  if (start && end > latestEndDate(start)) return 'tooFar'
  return null
}

/** The translation key that explains each end-date problem. */
export const END_DATE_PROBLEM_KEY: Record<RecurrenceEndDateProblem, string> = {
  required: 'meetings.schedule.recurrence.endDateRequired',
  beforeStart: 'meetings.schedule.recurrence.endDateBeforeStart',
  noMeetings: 'meetings.schedule.recurrence.endDateNoMeetings',
  tooFar: 'meetings.schedule.recurrence.endDateTooFar',
}

/**
 * The last day an end date may fall on: one year after the first meeting.
 * `addYears` takes 29 Feb to 28 Feb, as the server does, not to 1 March.
 */
export function latestEndDate(start: Date): Date {
  return addYears(startOfDay(start), RECURRENCE_MAX_END_YEARS)
}

// ── Summary line ───────────────────────────────────────────────────────────

const KEY = 'meetings.schedule.recurrence'

/**
 * The plural form a count takes.
 *
 * `single` is exactly one, so languages whose "one" category also covers 21 or
 * 31 (Russian) still get "every day" rather than "every 1 day". Beyond that,
 * only the languages that need more than singular/plural have extra forms in
 * the translations; everyone else uses `other`.
 */
const EXTRA_PLURAL_FORMS: Record<string, readonly string[]> = {
  ru: ['one', 'few', 'many'],
  ar: ['zero', 'two', 'few', 'many'],
}

export function pluralForm(lang: string, count: number): string {
  if (count === 1) return 'single'
  let category: string
  try {
    category = new Intl.PluralRules(lang).select(count)
  } catch {
    return 'other'
  }
  return EXTRA_PLURAL_FORMS[lang]?.includes(category) ? category : 'other'
}

export function plural(t: Translate, lang: string, key: string, count: number): string {
  return t(`${KEY}.${key}.${pluralForm(lang, count)}`, { count })
}

/** "Sep 28, 2026" in English, the local equivalent elsewhere. */
export function formatSummaryDate(d: Date, lang: string): string {
  return new Intl.DateTimeFormat(intlLocale(lang, 'date'), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d)
}

/** Short weekday names in Sun→Sat order, joined as a list: "Mon, Wed and Fri". */
export function formatWeekDays(days: number[], lang: string): string {
  const names = [...days]
    .sort((a, b) => a - b)
    // 4 Jan 2026 is a Sunday, so day i of that week has weekday index i.
    .map((d) =>
      new Intl.DateTimeFormat(intlLocale(lang, 'date'), { weekday: 'short' }).format(new Date(2026, 0, 4 + d)),
    )
  try {
    return new Intl.ListFormat(intlLocale(lang, 'list'), { style: 'long', type: 'conjunction' }).format(names)
  } catch {
    return names.join(', ')
  }
}

const EN_ORDINAL_SUFFIX: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' }

/** Day of month as the summary names it: "1st" in English, "1er" in French. */
export function formatMonthDay(day: number, lang: string): string {
  if (lang === 'en') {
    const cat = new Intl.PluralRules('en-US', { type: 'ordinal' }).select(day)
    return `${day}${EN_ORDINAL_SUFFIX[cat] ?? 'th'}`
  }
  if (lang === 'fr' && day === 1) return '1er'
  return String(day)
}

/**
 * The one-line description of a series, e.g.
 * "Every 3 weeks on Mon, Wed and Fri from Sep 28, 2026, 10 meetings."
 */
export function describeRecurrence(cfg: RecurringConfig, start: Date, lang: string, t: Translate): string {
  const frequency = RECURRENCE_FREQUENCIES.includes(cfg.frequency) ? cfg.frequency : 'weekly'
  const interval = Math.max(1, Math.round(Number(cfg.interval) || 1))
  const everyKey = frequency === 'daily' ? 'everyDay' : frequency === 'weekly' ? 'everyWeek' : 'everyMonth'
  const every = plural(t, lang, everyKey, interval)

  const endDate = cfg.endType === 'date' ? parseLocalDate(cfg.endDate) : undefined
  const end = endDate
    ? t(`${KEY}.until`, { date: formatSummaryDate(endDate, lang) })
    : plural(t, lang, 'summaryCount', Math.max(1, Math.round(Number(cfg.occurrences) || 1)))

  const vars = { every, start: formatSummaryDate(start, lang), end }

  let text: string
  if (frequency === 'weekly' && cfg.weekDays?.length) {
    text = t(`${KEY}.summaryOnDays`, { ...vars, days: formatWeekDays(cfg.weekDays, lang) })
  } else if (frequency === 'monthly') {
    // Days 29–31 fall on the last day of a shorter month, and the summary says
    // so rather than let "on the 31st" promise a date February does not have.
    const day = cfg.monthDay ?? start.getDate()
    text = t(`${KEY}.${day >= 29 ? 'summaryOnDayOrLast' : 'summaryOnDay'}`, { ...vars, day: formatMonthDay(day, lang) })
  } else {
    text = t(`${KEY}.summary`, vars)
  }
  // Some locales' dates end in an abbreviation ("31 дек. 2026 г."), which met
  // the sentence's own full stop as "г..".
  return text.replace(/\.\.$/, '.')
}

/**
 * The id a Join button should open.
 *
 * A one-off meeting joins by its code. An occurrence joins by its own id: its
 * code is the series', which opens whichever day is today, so the Join button
 * on next Wednesday's entry must name next Wednesday or it would open today's
 * meeting instead of saying Wednesday is not today. Shared links still use the
 * code, because that is the one link that works for the whole series.
 */
export function joinPathId(m: { id: string; meetingId?: string; series?: unknown }): string {
  return m.series ? m.id : (m.meetingId ?? m.id)
}

/**
 * Does `id` name this meeting? Its record id always does. Its code does only
 * for a meeting that is not one occurrence of a series: every occurrence shares
 * the series code, so matching on it would turn Mark as Past on one day into
 * every day of the series. No id names no meeting.
 */
export function matchesMeetingId(m: { id: string; meetingId?: string; series?: unknown }, id: string): boolean {
  return !!id && (m.id === id || (!m.series && m.meetingId === id))
}

/** A calendar day in `zone` as "2026-09-28"; the browser's own zone when `zone` is unknown. */
function dayKey(ms: number, zone: string | undefined): string {
  const opts = { year: 'numeric', month: '2-digit', day: '2-digit' } as const
  try {
    return new Intl.DateTimeFormat('en-CA', { ...opts, timeZone: zone }).format(ms)
  } catch {
    return new Intl.DateTimeFormat('en-CA', opts).format(ms)
  }
}

/**
 * The start of an occurrence that falls on a later day than today, which the
 * server refuses to join (OCCURRENCE_NOT_TODAY); null when it may be joined.
 * "Today" is the meeting's time zone, as on the server, so the Join button is
 * off exactly when pressing it would be refused.
 */
export function joinLockedUntil(
  m: { startTimeMs: number; timezone?: string; series?: { timezone?: string } },
  now = Date.now(),
): number | null {
  if (!m.series) return null
  const zone = m.series.timezone || m.timezone
  return dayKey(m.startTimeMs, zone) > dayKey(now, zone) ? m.startTimeMs : null
}

const OCCURRENCE_DAY: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }

/**
 * One occurrence's day as a dialog names it: "Wed, Sep 30" in English, or
 * "Wed, Sep 30, 2026" with `withYear`.
 */
export function formatOccurrenceDay(ms: number, lang: string, withYear = false): string {
  return new Intl.DateTimeFormat(intlLocale(lang), withYear ? { ...OCCURRENCE_DAY, year: 'numeric' } : OCCURRENCE_DAY).format(ms)
}

/** An occurrence's day and start time, in the viewer's zone: "Wed, Sep 30, 10:00 AM" in English. */
export function formatOccurrenceDateTime(ms: number, lang: string): string {
  try {
    return new Intl.DateTimeFormat(intlLocale(lang), { ...OCCURRENCE_DAY, hour: 'numeric', minute: '2-digit' }).format(ms)
  } catch {
    return new Date(ms).toLocaleString()
  }
}

/**
 * What an edit or delete of a day of a series applies to: that day, or every
 * day of the series that has not happened yet.
 */
export type SeriesScope = 'one' | 'all'

/**
 * The wording of the delete confirmation and its success toast. For one day of
 * a series it is a cancellation of that day, named, not a deletion; for all
 * occurrences, the cancellation of every upcoming day.
 */
export function deleteMeetingCopy(
  m: { title: string; startTimeMs: number; series?: unknown },
  t: Translate,
  lang: string,
  scope: SeriesScope = 'one',
): { title: string; warning: string; confirm: string; doneTitle: string; doneDescription: string } {
  if (!m.series) {
    return {
      title: t('meetings.deleteMeeting'),
      warning: t('meetings.deleteMeetingWarning'),
      confirm: t('common.delete'),
      doneTitle: t('meetings.meetingDeleted'),
      doneDescription: `"${m.title}" ${t('meetings.hasBeenRemoved')}`,
    }
  }
  if (scope === 'all') {
    return {
      title: t('meetings.series.scopeDeleteTitle'),
      warning: t('meetings.series.scopeAllDeleteHint'),
      confirm: t('common.delete'),
      doneTitle: t('meetings.series.seriesCancelled'),
      doneDescription: t('meetings.series.seriesCancelledDescription', { title: m.title }),
    }
  }
  const date = formatOccurrenceDay(m.startTimeMs, lang)
  return {
    title: t('meetings.series.cancelOccurrenceTitle'),
    warning: t('meetings.series.cancelOccurrenceWarning', { date }),
    confirm: t('meetings.series.cancelOccurrence'),
    doneTitle: t('meetings.series.occurrenceCancelled'),
    doneDescription: t('meetings.series.occurrenceCancelledDescription', {
      title: m.title,
      date: formatOccurrenceDay(m.startTimeMs, lang, true),
    }),
  }
}

/**
 * The meeting a URL id names, for screens that only have the id.
 *
 * An exact record id wins. A code is shared by every occurrence of a series,
 * so among several matches the one nearest to now is the one being held: the
 * server opens today's occurrence for a series code, and this agrees with it.
 */
export function findMeetingByUrlId<T extends { id: string; meetingId?: string; startTimeMs: number }>(
  meetings: T[],
  urlId: string | undefined,
  now: number = Date.now(),
): T | undefined {
  if (!urlId) return undefined
  const exact = meetings.find((m) => m.id === urlId)
  if (exact) return exact
  const byCode = meetings.filter((m) => m.meetingId === urlId)
  if (byCode.length <= 1) return byCode[0]
  return byCode.reduce((best, m) => (Math.abs(m.startTimeMs - now) < Math.abs(best.startTimeMs - now) ? m : best))
}
